import {
  DRAWING_SPEC_VERSION,
  type AxisFlips,
  type CompactDrawingSpec,
  type Defaults,
  type DrawingSpec,
  type DrawingSpecLineStyle,
  type EditorState,
  type Job,
  type JobViewId,
  type OutputSettings,
  type PartAnalysis,
  type MeasuredGeometry,
  type PartsReport,
  type SourcePmiGeometry,
  type SampleKey,
  type SheetSpline,
  type Upload,
  type ViewLines,
  type ViewerSelection,
} from "./contracts";
import type { Me, PlanOffer } from "./plan-access";
import type { CreateDocument, ProjectSummary, SavedProject } from "./create-model";

export type ExportFormat = "pdf" | "dwg" | "dxf";
export type ExportResponse = {
  preview_url: string;
  pdf_url: string | null;
  dwg_url: string | null;
  dxf_url: string | null;
};

/**
 * A refused request. `code` is set when the workspace can act on it:
 * `plan_required` names the `feature` the plan lacks, `quota_exceeded` the
 * `limit` reached.
 */
export class ApiProblem extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly feature?: string,
    readonly limit?: string,
  ) {
    super(message);
    this.name = "ApiProblem";
  }
}

const text = (value: unknown) => (typeof value === "string" ? value : undefined);

export function problemFrom(status: number, body: string) {
  try {
    const parsed: unknown = JSON.parse(body);
    if (typeof parsed === "object" && parsed !== null) {
      const fields = parsed as Record<string, unknown>;
      return new ApiProblem(
        text(fields.error) ?? `Request failed (${status}).`,
        status,
        text(fields.code),
        text(fields.feature),
        text(fields.limit),
      );
    }
  } catch {
    // Some proxy failures are plain text rather than JSON.
  }
  return new ApiProblem(`Request failed (${status}).`, status);
}

export async function accountRequest<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const response = await requestBody(path, {
    method,
    headers: { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return (response ? JSON.parse(response) : undefined) as T;
}

async function requestBody(path: string, init?: RequestInit) {
  return (await requestResponse(path, init)).body;
}

async function requestResponse(path: string, init?: RequestInit) {
  const headers = new Headers(init?.headers);
  if (init?.method && !["GET", "HEAD"].includes(init.method.toUpperCase())) {
    // Without a session answer the request goes out bare, and its own
    // refusal says what went wrong rather than a JSON parse error.
    const session = (await fetch("/api/auth/session", {
      cache: "no-store",
      signal: init.signal,
    }).then((response) => (response.ok ? response.json() : {}))) as { csrf?: string };
    if (session.csrf) headers.set("x-specbound-csrf", session.csrf);
  }
  const response = await fetch(path, { cache: "no-store", ...init, headers });
  const body = await response.text();
  if (!response.ok) throw problemFrom(response.status, body);
  return { body, headers: response.headers };
}

/**
 * An editor state and the save it was read from or made by. A save names the
 * one it started from, and is refused (`drawing_changed`) if the drawing was
 * saved again since, in another window or by someone with Edit access.
 */
export type SavedEditor = { editor: EditorState; savedAt: number | null };

async function requestSavedEditor(path: string, init?: RequestInit): Promise<SavedEditor> {
  const { body, headers } = await requestResponse(path, init);
  const savedAt = Number(headers.get("x-specbound-saved-at") ?? Number.NaN);
  return {
    editor: JSON.parse(body) as EditorState,
    savedAt: Number.isSafeInteger(savedAt) ? savedAt : null,
  };
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const body = await requestBody(path, init);
  return JSON.parse(body) as T;
}

async function requestText(path: string, init?: RequestInit): Promise<string> {
  return requestBody(path, init);
}

function waitForPoll(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const abort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, 1000);
    signal?.addEventListener("abort", abort, { once: true });
  });
}

async function requestAnalysis<T>(
  id: string,
  signal: AbortSignal | undefined,
  retry: boolean,
  part: "all" | "features" | "pmi",
): Promise<T> {
  const path = `/api/jobs/${encodeURIComponent(id)}/analysis`;
  for (;;) {
    const query = [retry ? "retry=true" : "", part === "all" ? "" : `${part}=true`]
      .filter(Boolean)
      .join("&");
    const response = await fetch(`${path}${query ? `?${query}` : ""}`, {
      cache: "no-store",
      signal,
    });
    retry = false;
    const body = await response.text();
    if (!response.ok) throw problemFrom(response.status, body);
    if (response.status !== 202) return JSON.parse(body) as T;
    await waitForPoll(signal);
  }
}

const compactLineStyles: Record<string, DrawingSpecLineStyle> = {
  v: "visible",
  b: "border",
  t: "title",
  h: "hidden",
  H: "hidden-deep",
  c: "center",
  d: "dimension",
  x: "hatch",
  k: "cutting",
};

export function expandCompactDrawingSpec(spec: CompactDrawingSpec): DrawingSpec {
  if (spec.format_version !== DRAWING_SPEC_VERSION) {
    throw new Error(
      "This drawing was made by an earlier version of Specbound. Delete it and convert the file again.",
    );
  }
  if (spec.encoding !== "flat-v1" || spec.coordinates.length !== spec.styles.length * 4) {
    throw new Error("The drawing geometry response is invalid.");
  }
  const lines = new Array<DrawingSpec["lines"][number]>(spec.styles.length);
  for (let index = 0; index < spec.styles.length; index += 1) {
    const style = compactLineStyles[spec.styles[index] ?? ""];
    if (!style) throw new Error("The drawing geometry response has an unknown line style.");
    const coordinate = index * 4;
    lines[index] = {
      x1: spec.coordinates[coordinate] as number,
      y1: spec.coordinates[coordinate + 1] as number,
      x2: spec.coordinates[coordinate + 2] as number,
      y2: spec.coordinates[coordinate + 3] as number,
      style,
    };
  }
  // Curves are optional: a spec without them draws its chords.
  const curves = (spec.curves ?? []).flatMap((values) =>
    values.length === 6 && values.every(Number.isFinite)
      ? [
          {
            c: [values[0], values[1]] as [number, number],
            a: [values[2], values[3]] as [number, number],
            b: [values[4], values[5]] as [number, number],
          },
        ]
      : [],
  );
  if (curves.length !== (spec.curves ?? []).length)
    throw new Error("The drawing geometry response has an invalid curve.");
  for (const [first, count, curve] of spec.curve_runs ?? []) {
    if (
      !Number.isInteger(first) ||
      !Number.isInteger(count) ||
      first < 0 ||
      count < 1 ||
      first + count > lines.length ||
      !curves[curve]
    )
      throw new Error("The drawing geometry response has an invalid curve run.");
    for (let index = first; index < first + count; index += 1)
      lines[index] = { ...lines[index], curve };
  }
  // Splines likewise: each a clamped NURBS curve of consistent size.
  const splines = spec.splines ?? [];
  if (!splines.every(validSpline))
    throw new Error("The drawing geometry response has an invalid spline.");
  for (const [first, count, spline] of spec.spline_runs ?? []) {
    if (
      !Number.isInteger(first) ||
      !Number.isInteger(count) ||
      first < 0 ||
      count < 1 ||
      first + count > lines.length ||
      !splines[spline]
    )
      throw new Error("The drawing geometry response has an invalid spline run.");
    for (let index = first; index < first + count; index += 1)
      lines[index] = { ...lines[index], spline };
  }
  // Section views' cut faces: closed rings of finite points on known views.
  for (const region of spec.regions ?? []) {
    if (
      typeof region.group !== "string" ||
      !spec.groups.some((group) => group.id === region.group) ||
      typeof region.hatched !== "boolean" ||
      !Array.isArray(region.rings) ||
      !region.rings.every(
        (ring) =>
          Array.isArray(ring) &&
          ring.every(
            (point) =>
              Array.isArray(point) &&
              point.length === 2 &&
              Number.isFinite(point[0]) &&
              Number.isFinite(point[1]),
          ),
      )
    )
      throw new Error("The drawing geometry response has an invalid cut face.");
  }
  const {
    encoding,
    coordinates,
    styles,
    curve_runs,
    curves: compactCurves,
    spline_runs,
    splines: compactSplines,
    ...drawing
  } = spec;
  // The compact fields were validated and expanded above; keep them out of
  // the browser-facing drawing contract.
  void encoding;
  void coordinates;
  void styles;
  void curve_runs;
  void compactCurves;
  void spline_runs;
  void compactSplines;
  return {
    ...drawing,
    lines,
    ...(curves.length ? { curves } : {}),
    ...(splines.length ? { splines } : {}),
  };
}

/** A clamped NURBS curve: knots in order, the first and last `degree + 1` equal, finite points, positive weights. */
function validSpline(spline: SheetSpline) {
  const { degree, knots, points, weights } = spline ?? {};
  if (
    !Number.isInteger(degree) ||
    degree < 1 ||
    !Array.isArray(knots) ||
    !Array.isArray(points) ||
    points.length <= degree ||
    knots.length !== points.length + degree + 1 ||
    !knots.every(Number.isFinite) ||
    !points.every(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1]),
    ) ||
    (weights !== undefined &&
      (!Array.isArray(weights) ||
        weights.length !== points.length ||
        !weights.every((weight) => Number.isFinite(weight) && weight > 0)))
  )
    return false;
  for (let index = 1; index < knots.length; index += 1)
    if (knots[index - 1]! > knots[index]!) return false;
  const last = knots.length - 1;
  for (let index = 1; index <= degree; index += 1)
    if (knots[index] !== knots[0] || knots[last - index] !== knots[last]) return false;
  return knots[degree]! < knots[points.length]!;
}

async function requestJsonWithUploadProgress<T>(
  path: string,
  form: FormData,
  onProgress?: (progress: number) => void,
): Promise<T> {
  const session = (await fetch("/api/auth/session", { cache: "no-store" }).then((response) =>
    response.json(),
  )) as { csrf?: string };
  return new Promise((resolve, reject) => {
    const startedAt = performance.now();
    let uploadCompletedAt: number | null = null;
    const xhr = new XMLHttpRequest();
    xhr.open("POST", path);
    if (session.csrf) xhr.setRequestHeader("x-specbound-csrf", session.csrf);
    xhr.responseType = "text";
    xhr.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return;
      const progress = Math.min(100, Math.round((event.loaded / event.total) * 100));
      if (progress === 100 && uploadCompletedAt === null) {
        uploadCompletedAt = performance.now();
        console.info("[upload] request body sent", {
          elapsed_ms: Math.round(uploadCompletedAt - startedAt),
          bytes: event.total,
        });
      }
      onProgress?.(progress);
    });
    xhr.addEventListener("load", () => {
      const body = xhr.responseText;
      const completedAt = performance.now();
      console.info("[upload] response received", {
        status: xhr.status,
        total_ms: Math.round(completedAt - startedAt),
        response_wait_ms: Math.round(completedAt - (uploadCompletedAt ?? completedAt)),
      });
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(problemFrom(xhr.status, body));
        return;
      }
      try {
        resolve(JSON.parse(body) as T);
      } catch {
        reject(new Error("The conversion service returned an invalid response."));
      }
    });
    xhr.addEventListener("error", () =>
      reject(new Error("Could not reach the conversion service.")),
    );
    xhr.addEventListener("abort", () => reject(new Error("Upload was cancelled.")));
    xhr.send(form);
  });
}

export const api = {
  me(signal?: AbortSignal, context?: { drawing?: string; project?: string }) {
    const query = new URLSearchParams(context);
    return requestJson<Me>("/api/me" + (query.size ? "?" + query.toString() : ""), { signal });
  },
  planOffer(signal?: AbortSignal) {
    return requestJson<PlanOffer>("/api/plans", { signal });
  },
  /** "Ask for access": the owner sees it in the admin area. */
  askForPlan(planId: string, feature: string) {
    return requestBody("/api/me/upgrade-requests", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ plan_id: planId, feature }),
    });
  },
  listJobs(signal?: AbortSignal) {
    return requestJson<Job[]>("/api/jobs", { signal });
  },
  listCreateProjects() {
    return requestJson<ProjectSummary[]>("/api/create-projects");
  },
  getCreateProject(id: string) {
    return requestJson<SavedProject>(`/api/create-projects/${encodeURIComponent(id)}`);
  },
  saveCreateProject(document: CreateDocument, saved: { id: string; revision: number } | null) {
    return requestJson<SavedProject>(
      `/api/create-projects${saved ? `/${encodeURIComponent(saved.id)}` : ""}`,
      {
        method: saved ? "PUT" : "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ document, revision: saved?.revision ?? null }),
      },
    );
  },
  deleteCreateProject(id: string) {
    return requestJson<{ status: string }>(`/api/create-projects/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  listUploads(signal?: AbortSignal) {
    return requestJson<Upload[]>("/api/uploads", { signal });
  },
  getJob(id: string, signal?: AbortSignal) {
    return requestJson<Job>(`/api/jobs/${encodeURIComponent(id)}`, { signal });
  },
  getViewLines(id: string, view: JobViewId, signal?: AbortSignal) {
    return requestJson<ViewLines>(`/api/jobs/${encodeURIComponent(id)}/views/${view}`, { signal });
  },
  /** Skip a view while it is being solved; the conversion moves on to the next. */
  skipView(id: string, view: JobViewId) {
    return requestJson<Job>(`/api/jobs/${encodeURIComponent(id)}/views/${view}/skip`, {
      method: "POST",
    });
  },
  /** Draw a view skipped from this drawing, without a credit; resolves when it is drawn. */
  async generateView(id: string, view: JobViewId) {
    const path = `/api/jobs/${encodeURIComponent(id)}`;
    let job = await requestJson<Job>(`${path}/views/${view}/generate`, { method: "POST" });
    while (job.status === "queued" || job.status === "running") {
      await waitForPoll();
      job = await requestJson<Job>(path);
    }
    if (job.status === "failed")
      throw new ApiProblem(job.error ?? "The view could not be drawn.", 500);
    return job;
  },
  deleteJob(id: string) {
    return requestJson<{ status: string }>(`/api/jobs/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  deleteUpload(id: string) {
    return requestJson<{ status: string; kept_drawings: number }>(
      `/api/uploads/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },
  duplicateJob(id: string) {
    return requestJson<Job>(`/api/jobs/${encodeURIComponent(id)}/duplicate`, { method: "POST" });
  },
  /** `power` asks for Power mode; the server applies it only where the plan has it. */
  createDrawingFromUpload(id: string, defaults: Defaults, power = true) {
    return requestJson<Job>(`/api/uploads/${encodeURIComponent(id)}/drawings`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        standard: defaults.standard,
        sheet_size: defaults.sheet_size,
        sheet_auto: defaults.sheet_auto !== false,
        sheet_orientation: defaults.sheet_orientation ?? "landscape",
        sheet_style: defaults.sheet_style,
        quality: defaults.quality,
        projection: defaults.projection,
        dimension_units: defaults.dimension_units,
        border_columns: defaults.border_columns,
        border_rows: defaults.border_rows,
        hidden_depth: defaults.hidden_depth,
        output_pdf: defaults.pdf,
        output_dwg: defaults.dwg,
        power,
      }),
    });
  },
  createJob(form: FormData, onUploadProgress?: (progress: number) => void) {
    return requestJsonWithUploadProgress<Job>("/api/jobs", form, onUploadProgress);
  },
  createSampleJob(defaults: Defaults, sample: SampleKey = "1600i", power = true) {
    return requestJson<Job>("/api/dev/sample-job", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        sample,
        standard: defaults.standard,
        sheet_size: defaults.sheet_size,
        sheet_auto: defaults.sheet_auto !== false,
        sheet_orientation: defaults.sheet_orientation ?? "landscape",
        sheet_style: defaults.sheet_style,
        quality: defaults.quality,
        projection: defaults.projection,
        dimension_units: defaults.dimension_units,
        border_columns: defaults.border_columns,
        border_rows: defaults.border_rows,
        hidden_depth: defaults.hidden_depth,
        output_pdf: defaults.pdf,
        output_dwg: defaults.dwg,
        power,
      }),
    });
  },
  getEditor(id: string, signal?: AbortSignal) {
    return requestSavedEditor(`/api/jobs/${encodeURIComponent(id)}/editor`, { signal });
  },
  saveEditor(id: string, editor: EditorState, savedAt: number | null) {
    const from = savedAt === null ? "" : `?saved_at=${savedAt}`;
    return requestSavedEditor(`/api/jobs/${encodeURIComponent(id)}/editor${from}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(editor),
    });
  },
  getPreview(id: string, signal?: AbortSignal) {
    return requestText(`/api/jobs/${encodeURIComponent(id)}/preview`, { signal });
  },
  getSpec(id: string, signal?: AbortSignal) {
    return requestJson<CompactDrawingSpec>(`/api/jobs/${encodeURIComponent(id)}/spec`, {
      signal,
    }).then(expandCompactDrawingSpec);
  },
  getAnalysis(id: string, signal?: AbortSignal, retry = false) {
    return requestAnalysis<PartAnalysis>(id, signal, retry, "all");
  },
  getAutoDimensionGeometry(id: string, signal?: AbortSignal) {
    return requestAnalysis<MeasuredGeometry>(id, signal, true, "features");
  },
  /** The STEP file's own semantic PMI, with the geometry each item belongs to. */
  getSourcePmi(id: string, signal?: AbortSignal) {
    return requestAnalysis<SourcePmiGeometry>(id, signal, true, "pmi");
  },
  /** Decline the upload's PMI offer; recorded at once, also while the views are made. */
  declineSourcePmi(id: string) {
    return requestJson<EditorState>(`/api/jobs/${encodeURIComponent(id)}/source-pmi`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ choice: "declined" }),
    });
  },
  /** The drawing's parts so far; the first request starts measuring them. */
  getParts(id: string, retry = false, signal?: AbortSignal) {
    return requestJson<PartsReport>(
      `/api/jobs/${encodeURIComponent(id)}/parts${retry ? "?retry=true" : ""}`,
      { signal, cache: "no-store" },
    );
  },
  partThumbnailUrl(id: string, index: number, version: number) {
    return `/api/jobs/${encodeURIComponent(id)}/parts/${index}/thumbnail?v=${version}`;
  },
  async recalculate(id: string) {
    const path = `/api/jobs/${encodeURIComponent(id)}`;
    let job = await requestJson<Job>(`${path}/recalculate`, { method: "POST" });
    while (job.status === "queued" || job.status === "running") {
      await waitForPoll();
      job = await requestJson<Job>(path);
    }
    if (job.status === "failed") throw new ApiProblem(job.error ?? "Recalculation failed.", 500);
    return job;
  },
  createDerivedDrawing(
    id: string,
    selection: ViewerSelection,
    axis_flips: AxisFlips,
    item_ids: number[] = [],
  ) {
    return requestJson<Job>(`/api/jobs/${encodeURIComponent(id)}/derived-drawings`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ selection, axis_flips, item_ids: [...new Set(item_ids)] }),
    });
  },
  /** A new drawing of whole solids; one solid's drawing is named `part_name`. */
  createDrawingFromItems(id: string, item_ids: number[], part_name?: string) {
    return requestJson<Job>(`/api/jobs/${encodeURIComponent(id)}/derived-drawings`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ item_ids: [...new Set(item_ids)], direct_items: true, part_name }),
    });
  },
  /** A new drawing of one part from the Parts page, in its own coordinates. */
  createDrawingFromPart(id: string, part: number) {
    return requestJson<Job>(`/api/jobs/${encodeURIComponent(id)}/derived-drawings`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ part }),
    });
  },
  exportDrawing(id: string, spec: DrawingSpec, output: OutputSettings, dxf = false) {
    return requestJson<ExportResponse>(`/api/jobs/${encodeURIComponent(id)}/export`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ spec, output, dxf }),
    });
  },
};
